import { DOC_IMAGE_MAX_BYTES, DOC_IMAGE_SIZES, type DocImageSize, TEMPLATE_LOGO_KEY, TEMPLATE_SCHEMA, type TemplateKind } from '@bqcrm/shared';
import { Button, FileButton, Menu, ScrollArea } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import { IconBraces, IconPhoto, IconTable } from '@tabler/icons-react';
import TextAlign from '@tiptap/extension-text-align';
import { useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef, useState } from 'react';
import { apiForm } from '../api';
import { notifyError } from '../notify';
import { GrafikaNode } from './docImages';

/**
 * Edytor treści dokumentu (TipTap) z wstawianiem pól danych {{…}} i bloków (tabele generowane z danych).
 * Wartość to HTML; pola zostają w treści jako znaczniki i są wypełniane przy podglądzie i wydaniu.
 */
export function RichEditor({
  value,
  onChange,
  editable = true,
  minHeight = 420,
  kind = 'umowa',
  imageUpload = false,
}: {
  value: string;
  onChange: (html: string) => void;
  editable?: boolean;
  minHeight?: number;
  /** rodzaj dokumentu — decyduje o dostępnych polach i blokach */
  kind?: TemplateKind;
  /** wgrywanie grafik do treści (uprawnienie szablony.edycja); logo firmy można wstawić zawsze */
  imageUpload?: boolean;
}) {
  const schema = TEMPLATE_SCHEMA[kind];
  const editor = useEditor({
    extensions: [StarterKit, TextAlign.configure({ types: ['heading', 'paragraph'] }), GrafikaNode],
    content: value,
    editable,
    // tylko zmiany użytkownika (edytor ma fokus) — samo wczytanie i normalizacja HTML nie oznacza dokumentu jako zmienionego
    onUpdate: ({ editor: e }) => {
      if (e.isFocused) onChange(e.getHTML());
    },
    immediatelyRender: true,
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value, { emitUpdate: false });
  }, [value, editor]);
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  const insert = (text: string) => editor?.chain().focus().insertContent(text).run();
  // rozmiar zaznaczonej grafiki (null — nie zaznaczono grafiki)
  const selectedSize = useEditorState({ editor, selector: ({ editor: e }) => (e?.isActive('grafika') ? ((e.getAttributes('grafika').rozmiar as DocImageSize) ?? 'm') : null) });
  const [uploading, setUploading] = useState(false);
  const resetFile = useRef<() => void>(null);
  const changed = () => editor && onChange(editor.getHTML());
  const upload = async (file: File) => {
    resetFile.current?.();
    if (file.size > DOC_IMAGE_MAX_BYTES) return notifyError(new Error('Grafika jest większa niż 1 MB — zmniejsz plik'));
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const r = await apiForm<{ fileId: string }>('/templates/images', form);
      editor?.chain().focus().insertContent({ type: 'grafika', attrs: { fileId: r.fileId, rozmiar: 'm' } }).run();
      changed();
    } catch (e) {
      notifyError(e);
    } finally {
      setUploading(false);
    }
  };
  const setSize = (rozmiar: DocImageSize) => {
    editor?.chain().focus().updateAttributes('grafika', { rozmiar }).run();
    changed();
  };

  return (
    <RichTextEditor editor={editor} styles={{ content: { minHeight } }}>
      {editable && (
        <RichTextEditor.Toolbar sticky stickyOffset={56}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.ClearFormatting />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H1 />
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
            <RichTextEditor.Hr />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.AlignLeft />
            <RichTextEditor.AlignCenter />
            <RichTextEditor.AlignJustify />
            <RichTextEditor.AlignRight />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Undo />
            <RichTextEditor.Redo />
          </RichTextEditor.ControlsGroup>
          <Menu position="bottom-start" withinPortal shadow="md">
            <Menu.Target>
              <Button size="compact-xs" variant="light" leftSection={<IconBraces size={14} />}>
                Wstaw pole
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <ScrollArea.Autosize mah={420}>
                {schema.groups.map((g) => (
                  <div key={g.label}>
                    <Menu.Label>{g.label}</Menu.Label>
                    {g.fields.map((f) => (
                      <Menu.Item key={f.key} onClick={() => insert(`{{${f.key}}}`)}>
                        {f.label}
                      </Menu.Item>
                    ))}
                  </div>
                ))}
                <Menu.Label>Sekcja warunkowa</Menu.Label>
                <Menu.Item onClick={() => insert(schema.condition.text)}>{schema.condition.label}</Menu.Item>
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
          <Menu position="bottom-start" withinPortal shadow="md">
            <Menu.Target>
              <Button size="compact-xs" variant="light" leftSection={<IconTable size={14} />}>
                Wstaw blok danych
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {schema.blocks.map((b) => (
                <Menu.Item key={b.key} onClick={() => editor?.chain().focus().insertContent(`<p>{{${b.key}}}</p>`).run()}>
                  {b.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <Menu position="bottom-start" withinPortal shadow="md">
            <Menu.Target>
              <Button size="compact-xs" variant="light" leftSection={<IconPhoto size={14} />}>
                Logo i grafika
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => insert(`{{${TEMPLATE_LOGO_KEY}}}`)}>Wstaw logo firmy wystawiającej dokument</Menu.Item>
              {selectedSize && (
                <>
                  <Menu.Label>Rozmiar zaznaczonej grafiki</Menu.Label>
                  {(Object.keys(DOC_IMAGE_SIZES) as DocImageSize[]).map((s) => (
                    <Menu.Item key={s} onClick={() => setSize(s)} fw={s === selectedSize ? 700 : undefined}>
                      {DOC_IMAGE_SIZES[s]}
                    </Menu.Item>
                  ))}
                </>
              )}
              {!selectedSize && <Menu.Label>Rozmiar: najpierw kliknij grafikę w treści</Menu.Label>}
              <Menu.Label>Położenie: przyciski wyrównania akapitu</Menu.Label>
            </Menu.Dropdown>
          </Menu>
          {imageUpload && (
            <FileButton onChange={(f) => f && void upload(f)} accept="image/png,image/jpeg" resetRef={resetFile}>
              {(props) => (
                <Button {...props} size="compact-xs" variant="light" leftSection={<IconPhoto size={14} />} loading={uploading} title="PNG albo JPG, do 1 MB">
                  Wgraj grafikę
                </Button>
              )}
            </FileButton>
          )}
        </RichTextEditor.Toolbar>
      )}
      <RichTextEditor.Content />
    </RichTextEditor>
  );
}

/** Podgląd dokumentu (HTML strony A4) w izolowanej ramce. */
export function DocumentPreview({ html, height = 640 }: { html: string; height?: number }) {
  return <iframe title="Podgląd dokumentu" srcDoc={html} sandbox="" style={{ width: '100%', height, border: '1px solid var(--app-line-strong)', borderRadius: 8, background: '#fff' }} />;
}
